import { Alert, Card, Col, Collapse, Descriptions, List, Row, Statistic, Table, Tag, Typography } from 'antd';
import { formatBytes, type OpsOverview } from '../../lib/ops-types';

function KeyList({ items }: { items: string[] }) {
  if (!items.length) return <Typography.Text type="secondary">없음</Typography.Text>;
  return <List size="small" dataSource={items} renderItem={(k) => <List.Item><Typography.Text code>{k}</Typography.Text></List.Item>} />;
}

export function OverviewTab({ data }: { data: OpsOverview }) {
  const { firestore, searchImages, r2, links } = data;
  return (
    <Row gutter={[16, 16]}>
      <Col xs={24} md={8}>
        <Card><Statistic title="검색 이미지 (Firestore)" value={searchImages.total} suffix="건" /></Card>
      </Col>
      <Col xs={24} md={8}>
        <Card><Statistic title="점검 필요 문서" value={searchImages.problemCount} suffix="건" valueStyle={searchImages.problemCount ? { color: '#d46b08' } : undefined} /></Card>
      </Col>
      <Col xs={24} md={8}>
        <Card><Statistic title="R2 객체" value={r2 ? r2.total : '-'} suffix={r2 ? `개 · ${formatBytes(r2.totalBytes)}` : ''} /></Card>
      </Col>

      {!r2 && (
        <Col span={24}>
          <Alert type="warning" showIcon message="R2 조회 자격증명이 설정되지 않아 CDN 정보와 연결 상태를 표시하지 않습니다." />
        </Col>
      )}

      {r2 && (
        <Col xs={24} lg={12}>
          <Card title="앱 리소스 배포 (latest.json)">
            <Descriptions column={1} size="small">
              <Descriptions.Item label="현재 릴리스">{r2.latest?.releaseId ?? '-'}</Descriptions.Item>
              <Descriptions.Item label="스토어 공개 버전 포인터">{r2.latest?.latestAppVersion ?? '-'}</Descriptions.Item>
              <Descriptions.Item label="현재 릴리스 기본 리소스">{r2.catalogResourceCount}개</Descriptions.Item>
              <Descriptions.Item label="보관된 릴리스">{r2.releases.length}개</Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
      )}

      <Col xs={24} lg={r2 ? 12 : 24}>
        <Card title="Firestore 컬렉션">
          <Table
            size="small"
            pagination={false}
            rowKey="id"
            dataSource={firestore.collections}
            columns={[
              { title: '컬렉션', dataIndex: 'id', render: (v: string) => <Typography.Text code>{v}</Typography.Text> },
              { title: '문서 수', dataIndex: 'count', align: 'right' },
              { title: '내용 조회', dataIndex: 'contentVisible', render: (v: boolean) => (v ? <Tag color="blue">조회</Tag> : <Tag>건수만</Tag>) },
            ]}
          />
        </Card>
      </Col>

      {r2 && (
        <Col xs={24} lg={12}>
          <Card title="R2 경로별 현황">
            <Table
              size="small"
              pagination={false}
              rowKey="prefix"
              dataSource={r2.byPrefix}
              columns={[
                { title: '경로', dataIndex: 'prefix', render: (v: string) => <Typography.Text code>{v}</Typography.Text> },
                { title: '객체 수', dataIndex: 'count', align: 'right' },
                { title: '용량', dataIndex: 'bytes', align: 'right', render: formatBytes },
              ]}
            />
          </Card>
        </Col>
      )}

      <Col xs={24} lg={12}>
        <Card title="검색 이미지 경로별">
          <Table
            size="small"
            pagination={false}
            rowKey="path"
            dataSource={searchImages.paths}
            columns={[
              { title: '경로(path)', dataIndex: 'path', render: (v: string) => <Typography.Text code>{v}</Typography.Text> },
              { title: '문서 수', dataIndex: 'count', align: 'right' },
            ]}
          />
          <Typography.Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>고유 태그 {searchImages.distinctTags}개</Typography.Paragraph>
        </Card>
      </Col>

      {links && (
        <Col span={24}>
          <Card title="R2 ↔ Firestore ↔ 카탈로그 연결">
            <Collapse
              items={[
                { key: 'sm', label: `검색 이미지 중 R2에 파일이 없는 항목 ${links.searchMissingInR2.length}건`, children: <KeyList items={links.searchMissingInR2} /> },
                { key: 'cm', label: `현재 카탈로그 중 R2에 파일이 없는 항목 ${links.catalogMissingInR2.length}건`, children: <KeyList items={links.catalogMissingInR2} /> },
                { key: 'sh', label: `카탈로그와 검색 이미지가 같은 파일을 쓰는 항목 ${links.sharedKeys.length}건`, children: <KeyList items={links.sharedKeys} /> },
                {
                  key: 'un',
                  label: `현재 릴리스·검색 이미지 어디에서도 참조하지 않는 이미지 ${links.unreferencedImages.length}건`,
                  children: (
                    <>
                      <Typography.Paragraph type="secondary">이전 릴리스나 이전 앱 버전에서 쓰였을 수 있으므로 미사용으로 단정하지 않습니다.</Typography.Paragraph>
                      <KeyList items={links.unreferencedImages} />
                    </>
                  ),
                },
              ]}
            />
          </Card>
        </Col>
      )}
    </Row>
  );
}
